import { Spinner, theme } from "@spartacommodities/sparta-react-core-components";
import { AxiosError } from "axios";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { LoaderFunctionArgs } from "react-router-dom";
import { Navigate, redirect, useFetcher, useRouteError, useRouteLoaderData, useSearchParams } from "react-router-dom";
import { setDatadogUserInfo } from "src/utils/datadogRumInit.ts";
import Input, { InputStatus } from "../../components/Input/Input.tsx";
import PhoneInput from "../../components/PhoneInput/PhoneInput.tsx";
import Select from "../../components/Select/Select.tsx";
import { PRIMARY_PRODUCTS, REGIONS } from "../../data";
import { useDatadog } from "../../hooks/useDatadog.ts";
import {
  CHECKBOX1_LINK,
  CHECKBOX1_MESSAGE,
  CHECKBOX_API_MESSAGE,
  CHECKBOX_LOWER_DESCRIPTION,
  PRIVACY_POLICY_LINK,
} from "../../literals.ts";
import Checkbox from "../../components/Checkbox/Checkbox.tsx";
import type { CurvesFormState } from "../../types";
import type { defaultActionForm } from "../utils/defaultActionForm.ts";
import { defaultErrorBoundary } from "../utils/defaultErrorBoundary.tsx";
import { getDefaultCurvesData } from "../utils/getDefaultCurvesData.ts";
import { trackCheckboxChange, trackPrimaryProductChange, trackRegionChange } from "../utils/inputTrack.ts";
import { trackFieldClicked, trackFieldFilled, trackOpenPrivacyPolicy } from "src/segment";

import { button, link, loadingButton, primaryButton, secondaryLink } from "src/styles.css.ts";
import * as styles from "../formStyles.css.ts";
import { getIPInfo } from "src/services/ipAPI.ts";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.ts";

export function CurvesSignupErrorBoundary() {
  const [searchParams] = useSearchParams();
  const error = useRouteError();

  /**
   * Now we get the error caught by the error boundary. We check if the error is a Response and if the status is 405.
   * We also check if the statusText is "curves is not allowed". If so, we redirect the user to the home page.
   *
   * This way we can let React-Router handle the flow. At the end, it's the same thing you would do with a server.
   */
  if (error instanceof Response && error.status === 405 && error.statusText === "curves is not allowed") {
    // Handle the error response here
    return <Navigate to="/" />;
  }

  const { axiosError, userData } = error as { axiosError: AxiosError; userData: Record<string, string> };

  if (axiosError instanceof AxiosError) {
    return defaultErrorBoundary({ userData }, new URLSearchParams());
  }
  return defaultErrorBoundary({ userData }, searchParams);
}

export async function curvesLoader({ request }: LoaderFunctionArgs) {
  const { country_calling_code } = await getIPInfo();

  const initialData = getDefaultCurvesData(request);

  return { country_calling_code, initialData };
}

export default function CurvesForm() {
  /**
   * Default trial type for the Curves form.
   *
   * This constant is used to specify the default trial type for the Curves signup process.
   */
  const DEFAULT_TRIAL_TYPE = "curves";
  /**
   * This is a trial feature flag for curves. The ideal situation were to have access to this feature flag on the loader,
   * but we don't have it yet. So we are checking it here and throwing a redirect if the user is not allowed to access
   * this page.
   */
  const isAllowed = useFeatureFlag("fe_curves_trial");

  if (!isAllowed) {
    /**
     * If the feature flag is not set, we redirect the user to the home page. The reason to throw a redirect here is to
     * break the render cycle and let the error boundary handle the error.
     *
     * As we redirect, we will be using a Response, so we use the HTTP error code 405 to indicate that the request
     * is not allowed. Web standards 💿
     */
    throw redirect("/", { status: 405, statusText: "curves is not allowed" });
  }

  const formRef = useRef<HTMLFormElement>(null);

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (formRef.current === null) return;
    // check validity on initial load,
    // for e.g. parameters
    setIsValid(formRef.current.checkValidity());
  }, []);

  useDatadog();

  const { country_calling_code, initialData } = useRouteLoaderData("curves-signup") as Awaited<
    ReturnType<typeof curvesLoader>
  >;

  const fetcher = useFetcher();
  const { errors, formState } =
    (fetcher.data as Awaited<ReturnType<typeof defaultActionForm<CurvesFormState>>> | undefined) || {};

  const handleOnFormChange = (currentTarget: HTMLFormElement) => {
    setIsValid(currentTarget.checkValidity());
  };

  return (
    <div className={styles.signupContainer} data-testid="signup-page">
      <section className={styles.sectionContainer}>
        <header className={styles.logo}>
          <img alt="Sparta" src="/svgs/curves-logo.svg" />
        </header>

        <article className={styles.article}>
          <div className={styles.message}>
            <p className={styles.title}>Experience Sparta Curves free for 45 Days!</p>
            <p className={styles.description} data-testid="description">
              Sign up today and enjoy a 45-day free trial with full access to all the features that have made us a
              trusted choice for traders around the globe. No risk, and instant access. Start your free trial Today!
            </p>
          </div>

          <fetcher.Form
            id="curves-form"
            method="POST"
            noValidate
            ref={formRef}
            className={styles.formContainer}
            onChange={(e) => handleOnFormChange(e.currentTarget)}
          >
            <div className={styles.formContent}>
              <div className={styles.inputContent}>
                <div className={styles.formRow}>
                  <input name="trialType" defaultValue={DEFAULT_TRIAL_TYPE} hidden />
                  <input name="userRole" defaultValue={initialData.userRole ?? 12} hidden />
                  <input name="primaryDataProvider" defaultValue={initialData.primaryDataProvider ?? "DP_1"} hidden />

                  <div className={styles.formRowDouble}>
                    <Input
                      placeholder="First Name"
                      name="firstName"
                      id="firstName"
                      required
                      defaultValue={initialData?.firstName ?? formState?.firstName ?? undefined}
                      onBlur={() =>
                        trackFieldFilled({
                          inputName: "First Name",
                          inputValue: formState?.firstName ?? "",
                          trialType: DEFAULT_TRIAL_TYPE,
                        })
                      }
                      onClick={() => trackFieldClicked("First Name", DEFAULT_TRIAL_TYPE)}
                      status={errors?.firstName ? InputStatus.ERROR : InputStatus.DEFAULT}
                      helper={{ id: "firstName-message", text: errors?.firstName ?? " " }}
                    />

                    <Input
                      placeholder="Last Name"
                      name="lastName"
                      id="lastName"
                      required
                      defaultValue={initialData.lastName ?? formState?.lastName ?? undefined}
                      onBlur={() =>
                        trackFieldFilled({
                          inputName: "Last Name",
                          inputValue: formState?.lastName ?? "",
                          trialType: DEFAULT_TRIAL_TYPE,
                        })
                      }
                      onClick={() => trackFieldClicked("Last Name", DEFAULT_TRIAL_TYPE)}
                      status={errors?.lastName ? InputStatus.ERROR : InputStatus.DEFAULT}
                      helper={{ id: "lastName-message", text: errors?.lastName ?? " " }}
                    />
                  </div>

                  <Input
                    type="text"
                    placeholder="Company Name"
                    name="companyName"
                    id="companyName"
                    required
                    onBlur={() =>
                      trackFieldFilled({
                        inputName: "Company Name",
                        inputValue: formState?.companyName ?? "",
                        trialType: DEFAULT_TRIAL_TYPE,
                      })
                    }
                    onClick={() => trackFieldClicked("Company Name", DEFAULT_TRIAL_TYPE)}
                    defaultValue={initialData?.companyName ?? formState?.companyName ?? undefined}
                    status={errors?.companyName ? InputStatus.ERROR : InputStatus.DEFAULT}
                    helper={{ id: "companyName-message", text: errors?.companyName ?? " " }}
                  />
                </div>

                <div className={styles.formRow}>
                  <Input
                    autoComplete="email"
                    placeholder="Email"
                    name="email"
                    id="email"
                    helper={{ id: "email-message", text: errors?.email ?? " " }}
                    type="email"
                    required
                    onBlur={(event) => {
                      setDatadogUserInfo(event.currentTarget.value);
                      trackFieldFilled({
                        inputName: "Email",
                        inputValue: formState?.email ?? "",
                        trialType: DEFAULT_TRIAL_TYPE,
                      });
                    }}
                    onClick={() => trackFieldClicked("Email", DEFAULT_TRIAL_TYPE)}
                    defaultValue={initialData.email ?? formState?.email ?? undefined}
                    status={(errors?.email ?? errors?.email) ? InputStatus.ERROR : InputStatus.DEFAULT}
                  />

                  <Select
                    type="text"
                    placeholder="Primary Product"
                    name="primaryProductName"
                    id="primaryProductName"
                    required
                    computeLabel={(p) => p.displayName ?? p.name}
                    value={PRIMARY_PRODUCTS.find((p) => p.name === initialData.primaryProductName || p.name === formState?.primaryProductName )}
                    status={errors?.primaryProduct ? InputStatus.ERROR : InputStatus.DEFAULT}
                    helper={{ id: "primary-product-message", text: errors?.primaryProduct ?? " " }}
                    options={PRIMARY_PRODUCTS}
                    onChange={(newPrimaryProduct) => {
                      if (formRef.current) handleOnFormChange(formRef.current);
                      trackPrimaryProductChange({ value: newPrimaryProduct.name, name: newPrimaryProduct.name });
                    }}
                    onClick={() => trackFieldClicked("Primary product", DEFAULT_TRIAL_TYPE)}
                  />
                </div>

                <div className={styles.formRow}>
                  <PhoneInput
                    placeholder="Phone"
                    name="phoneNumber"
                    id="phoneNumber"
                    required
                    onBlur={() =>
                      trackFieldFilled({
                        inputName: "Mobile",
                        inputValue: formState?.phoneNumber ?? "",
                        trialType: DEFAULT_TRIAL_TYPE,
                      })
                    }
                    onClick={() => trackFieldClicked("Mobile", DEFAULT_TRIAL_TYPE)}
                    onClickCountrySelector={() => trackFieldClicked("Country", DEFAULT_TRIAL_TYPE)}
                    onSelectCountry={(country) =>
                      trackFieldFilled({
                        inputName: "Country",
                        inputValue: country.name,
                        trialType: DEFAULT_TRIAL_TYPE,
                      })
                    }
                    value={initialData.phoneNumber ?? formState?.phoneNumber ?? country_calling_code}
                    status={errors?.phoneNumber ? InputStatus.ERROR : undefined}
                    helper={{ id: "phoneNumber-message", text: errors?.phoneNumber ?? " " }}
                  />

                  <Select
                    type="text"
                    placeholder="Region"
                    name="region"
                    id="region"
                    required
                    value={initialData.region ?? formState?.region ?? undefined}
                    status={errors?.region ? InputStatus.ERROR : InputStatus.DEFAULT}
                    helper={{ id: "region-message", text: errors?.region ?? " " }}
                    options={REGIONS}
                    onChange={(newRegion) => {
                      if (formRef.current) handleOnFormChange(formRef.current);
                      trackRegionChange({ value: newRegion, name: newRegion }, DEFAULT_TRIAL_TYPE);
                    }}
                    onClick={() => trackFieldClicked("Region", DEFAULT_TRIAL_TYPE)}
                  />
                </div>
              </div>
              <div className={styles.checkboxesContent}>
                <Checkbox
                  className={styles.checkboxOneLine}
                  name="apiInterest"
                  id="apiInterest"
                  defaultChecked={initialData.apiInterest ?? formState?.apiInterest}
                  onChange={trackCheckboxChange("apiInterest", DEFAULT_TRIAL_TYPE)}
                >
                  <p className={clsx(styles.text, styles.checkboxLabel)}>{CHECKBOX_API_MESSAGE}</p>
                </Checkbox>
                <Checkbox
                  name="subscribeNewsletter"
                  id="subscribeNewsletter"
                  defaultChecked={initialData.subscribeNewsletter ?? formState?.subscribeNewsletter}
                  onChange={trackCheckboxChange("subscribeNewsletter", DEFAULT_TRIAL_TYPE)}
                >
                  <p className={clsx(styles.text, styles.checkboxLabel)}>
                    {CHECKBOX1_MESSAGE}
                    <a
                      className={clsx(link, secondaryLink, styles.text, styles.checkboxLabel)}
                      href={PRIVACY_POLICY_LINK}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackOpenPrivacyPolicy(DEFAULT_TRIAL_TYPE)}
                    >
                      {CHECKBOX1_LINK}
                    </a>
                  </p>
                </Checkbox>
                <p className={clsx(styles.text, styles.underCheckboxDescription)} data-testid="disclaimer">
                  {CHECKBOX_LOWER_DESCRIPTION}
                </p>
              </div>
            </div>

            <button
              className={clsx(button, primaryButton, styles.submitButton, fetcher.state !== "idle" && loadingButton)}
              type="submit"
              disabled={!isValid}
              aria-disabled={!isValid}
            >
              {fetcher.state !== "idle" ? (
                <Spinner width={23} height={23} backgroundColor={theme.colors.neutral.dark_00} />
              ) : (
                "Sign up"
              )}
            </button>
          </fetcher.Form>
        </article>
      </section>
    </div>
  );
}
